import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useRules } from '../context/RuleContext';

const EMPTY_RULE = {
  carrier: '',
  sequenceNum: '',
  origin: '',
  destination: '',
  feePercent: '',
  feeAmount: '',
  suppress: '',
  tripType: '',
  cabinClass: '',
  passengerType: '',
  priceSource: '',
  travelDateEffective: '',
  travelDateDiscontinue: '',
  minPrice: '',
  maxPrice: '',
  minSegments: '',
  interline: '',
  sliceInterline: '',
};

export default function TestRules() {
  const { testRuleSet, addTestFeeRule, updateTestFeeRule, deleteTestFeeRule, addTestCarrierGroup, deleteTestCarrierGroup } = useRules();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE);
  const [carrierDialogOpen, setCarrierDialogOpen] = useState(false);
  const [carrierGroupNum, setCarrierGroupNum] = useState('');
  const [carrierInput, setCarrierInput] = useState('');
  const [carriers, setCarriers] = useState([]);

  const testRules = testRuleSet?.feeRules || [];
  const testCarrierGroups = testRuleSet?.carrierGroups || [];
  const testLocationGroups = testRuleSet?.locationGroups || [];

  const handleOpenAdd = () => {
    const maxSeq = testRules.reduce((max, r) => Math.max(max, r.sequenceNum || 0), 0);
    setForm({ ...EMPTY_RULE, sequenceNum: String(maxSeq + 1) });
    setEditingRule(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (rule) => {
    setForm({
      ...EMPTY_RULE,
      ...rule,
      sequenceNum: String(rule.sequenceNum || ''),
      feePercent: rule.feePercent != null ? String(rule.feePercent) : '',
      minSegments: rule.minSegments != null ? String(rule.minSegments) : '',
    });
    setEditingRule(rule);
    setDialogOpen(true);
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveRule = () => {
    if (!form.carrier || !form.sequenceNum) return;

    const rule = { carrier: form.carrier.trim(), sequenceNum: parseInt(form.sequenceNum) };
    if (form.origin) rule.origin = form.origin.trim();
    if (form.destination) rule.destination = form.destination.trim();
    if (form.feePercent) rule.feePercent = parseFloat(form.feePercent);
    if (form.feeAmount) rule.feeAmount = form.feeAmount.trim();
    if (form.suppress === 'true') rule.suppress = 'true';
    if (form.tripType) rule.tripType = form.tripType;
    if (form.cabinClass) rule.cabinClass = form.cabinClass;
    if (form.passengerType) rule.passengerType = form.passengerType;
    if (form.priceSource) rule.priceSource = form.priceSource;
    if (form.travelDateEffective) rule.travelDateEffective = form.travelDateEffective;
    if (form.travelDateDiscontinue) rule.travelDateDiscontinue = form.travelDateDiscontinue;
    if (form.minPrice) rule.minPrice = form.minPrice.trim();
    if (form.maxPrice) rule.maxPrice = form.maxPrice.trim();
    if (form.minSegments) rule.minSegments = parseInt(form.minSegments);
    if (form.interline) rule.interline = form.interline;
    if (form.sliceInterline) rule.sliceInterline = form.sliceInterline;

    if (editingRule) {
      updateTestFeeRule(editingRule.sequenceNum, rule);
    } else {
      addTestFeeRule(rule);
    }
    setDialogOpen(false);
  };

  const handleDelete = (seqNum) => {
    if (window.confirm(`Delete test rule #${seqNum}?`)) {
      deleteTestFeeRule(seqNum);
    }
  };

  // Carrier group
  const handleAddCarrier = () => {
    const code = carrierInput.trim().toUpperCase();
    if (!code || !/^[A-Z0-9]{2}$/.test(code)) return;
    if (carriers.includes(code)) return;
    setCarriers([...carriers, code]);
    setCarrierInput('');
  };

  const handleSaveCarrierGroup = () => {
    if (!carrierGroupNum || carriers.length === 0) return;
    addTestCarrierGroup({ tableNum: carrierGroupNum, carriers });
    setCarrierDialogOpen(false);
    setCarrierGroupNum('');
    setCarriers([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Test Rule Set</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add Test Rule
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>TestRuleSet</strong> is for testing only — does NOT affect Google Flights.
        Location groups, carrier groups, and fee rules from the main RuleSet are NOT included here.
      </Alert>

      {/* Test Carrier Groups */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Test Carrier Groups ({testCarrierGroups.length})</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCarrierDialogOpen(true)}>Add Group</Button>
        </Box>
        {testCarrierGroups.length > 0 ? (
          <Grid container spacing={2}>
            {testCarrierGroups.map(group => (
              <Grid item xs={12} sm={6} md={4} key={group.tableNum}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="primary">Group #{group.tableNum}</Typography>
                      <IconButton size="small" color="error" onClick={() => deleteTestCarrierGroup(group.tableNum)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {group.carriers.map(c => <Chip key={c} label={c} size="small" />)}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">No test carrier groups.</Typography>
        )}
      </Box>

      {/* Test Location Groups */}
      {testLocationGroups.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Test Location Groups ({testLocationGroups.length})</Typography>
          <Grid container spacing={2}>
            {testLocationGroups.map(group => (
              <Grid item xs={12} sm={6} md={4} key={group.tableNum}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary">Group #{group.tableNum}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {group.locations.map(l => <Chip key={l} label={l} size="small" />)}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Test Fee Rules Table */}
      <Typography variant="h6" sx={{ mb: 1 }}>Test Fee Rules ({testRules.length})</Typography>
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Seq#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Carrier</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cabin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Travel Dates</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Segments</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No test rules. Click "Add Test Rule" to create one.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                testRules.map((rule) => (
                  <TableRow key={rule.sequenceNum} hover>
                    <TableCell><strong>{rule.sequenceNum}</strong></TableCell>
                    <TableCell><Chip label={rule.carrier} size="small" variant="outlined" /></TableCell>
                    <TableCell>{rule.origin || '—'}</TableCell>
                    <TableCell>{rule.destination || '—'}</TableCell>
                    <TableCell>
                      {rule.suppress === 'true' ? (
                        <Chip label="SUPPRESS" color="error" size="small" />
                      ) : (
                        <span>
                          {rule.feePercent != null ? `${rule.feePercent}%` : ''}
                          {rule.feeAmount ? ` ${rule.feeAmount}` : ''}
                          {!rule.feePercent && !rule.feeAmount ? '—' : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{rule.cabinClass || '—'}</TableCell>
                    <TableCell>
                      {rule.travelDateEffective || rule.travelDateDiscontinue ? (
                        <Typography variant="caption">
                          {rule.travelDateEffective || '...'} → {rule.travelDateDiscontinue || '...'}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>{rule.minSegments || '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(rule)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(rule.sequenceNum)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Test Rule Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRule ? `Edit Test Rule #${editingRule.sequenceNum}` : 'Add Test Rule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Carrier" value={form.carrier} onChange={handleChange('carrier')} required helperText="e.g. LH, **, U:1" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth type="number" label="Sequence #" value={form.sequenceNum} onChange={handleChange('sequenceNum')} required inputProps={{ min: 1 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Fee Type</InputLabel>
                <Select
                  value={form.suppress === 'true' ? 'suppress' : form.feePercent ? 'percent' : form.feeAmount ? 'amount' : ''}
                  label="Fee Type"
                  onChange={(e) => {
                    if (e.target.value === 'suppress') setForm(f => ({ ...f, suppress: 'true', feePercent: '', feeAmount: '' }));
                    else if (e.target.value === 'percent') setForm(f => ({ ...f, suppress: '', feeAmount: '' }));
                    else if (e.target.value === 'amount') setForm(f => ({ ...f, suppress: '', feePercent: '' }));
                    else setForm(f => ({ ...f, suppress: '', feePercent: '', feeAmount: '' }));
                  }}
                  displayEmpty
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="percent">Percentage</MenuItem>
                  <MenuItem value="amount">Flat Amount</MenuItem>
                  <MenuItem value="suppress">Suppress</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {form.suppress !== 'true' && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Fee Percent (%)" type="number" value={form.feePercent} onChange={handleChange('feePercent')} inputProps={{ step: '0.1' }} InputLabelProps={{ shrink: true }} placeholder="e.g. 1.0" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Fee Amount" value={form.feeAmount} onChange={handleChange('feeAmount')} InputLabelProps={{ shrink: true }} placeholder="e.g. USD5.00" />
                </Grid>
              </>
            )}

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Origin" value={form.origin} onChange={handleChange('origin')} InputLabelProps={{ shrink: true }} placeholder="e.g. N:US, U:1" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Destination" value={form.destination} onChange={handleChange('destination')} InputLabelProps={{ shrink: true }} placeholder="e.g. N:GB" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Cabin Class</InputLabel>
                <Select value={form.cabinClass} label="Cabin Class" onChange={handleChange('cabinClass')} displayEmpty>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="ECONOMY">Economy</MenuItem>
                  <MenuItem value="BUSINESS">Business</MenuItem>
                  <MenuItem value="PREMIUM">Premium</MenuItem>
                  <MenuItem value="FIRST">First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Trip Type</InputLabel>
                <Select value={form.tripType} label="Trip Type" onChange={handleChange('tripType')} displayEmpty>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="ONE_WAY">One Way</MenuItem>
                  <MenuItem value="ROUND_TRIP">Round Trip</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Price Source</InputLabel>
                <Select value={form.priceSource} label="Price Source" onChange={handleChange('priceSource')} displayEmpty>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="GDS">GDS</MenuItem>
                  <MenuItem value="WEBSITE">Website</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth type="number" label="Min Segments" value={form.minSegments} onChange={handleChange('minSegments')} inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth type="date" label="Travel From" value={form.travelDateEffective} onChange={handleChange('travelDateEffective')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth type="date" label="Travel Until" value={form.travelDateDiscontinue} onChange={handleChange('travelDateDiscontinue')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Min Price" value={form.minPrice} onChange={handleChange('minPrice')} InputLabelProps={{ shrink: true }} placeholder="e.g. USD0" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Max Price" value={form.maxPrice} onChange={handleChange('maxPrice')} InputLabelProps={{ shrink: true }} placeholder="e.g. USD500" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRule} disabled={!form.carrier || !form.sequenceNum}>
            {editingRule ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Carrier Group Dialog */}
      <Dialog open={carrierDialogOpen} onClose={() => setCarrierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Test Carrier Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Table Number" type="number" value={carrierGroupNum}
            onChange={(e) => setCarrierGroupNum(e.target.value)}
            sx={{ mt: 1, mb: 2 }} InputLabelProps={{ shrink: true }}
            helperText="Referenced in test rules as U:N"
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth label="Carrier Code" value={carrierInput}
              onChange={(e) => setCarrierInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCarrier(); } }}
              InputLabelProps={{ shrink: true }}
              placeholder="2-letter IATA code"
            />
            <Button variant="outlined" onClick={handleAddCarrier}>Add</Button>
          </Box>
          {carriers.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {carriers.map(c => <Chip key={c} label={c} onDelete={() => setCarriers(carriers.filter(x => x !== c))} />)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarrierDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCarrierGroup} disabled={!carrierGroupNum || carriers.length === 0}>Add Group</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
